import { useState, useEffect, useCallback } from 'react';
import { GridColumns, GridRowModel } from '@mui/x-data-grid';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { LaundryCategory, LaundryGarment } from '../../entities';
import useGarments from '../../hooks/data/useGarments';
import useCategories from '../../hooks/data/useCategories';
import { DataTable } from '../../components';
import { useFetch } from '../../hooks';
import MainContainer from '../../components/MainContainer';

export const Garments = (): JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const [garments, setGarments] = useState<LaundryGarment[]>([] as LaundryGarment[]);
  const [categories, setCategories] = useState<LaundryCategory[]>([] as LaundryCategory[]);
  const { enqueueSnackbar } = useSnackbar();
  const garmentsRepository = useGarments(id!);
  const categoriesRepository = useCategories(id!);
  const { data: garmentsData, refetch: refetchGarments, loading } = useFetch(garmentsRepository.fetchAll);
  const { data: categoriesData } = useFetch(categoriesRepository.fetchAll);

  useEffect(() => {
    setGarments(garmentsData ?? []);
  }, [garmentsData]);

  useEffect(() => {
    setCategories(categoriesData ?? []);
  }, [categoriesData]);

  const processRowUpdate = useCallback(
    async (newRow: GridRowModel) => {
      try {
        enqueueSnackbar('Salvando a vestimenta...', { variant: 'info' });
        await garmentsRepository.update(newRow as LaundryGarment);
        enqueueSnackbar('Vestimenta salva.', { variant: 'success' });

        refetchGarments();

        return newRow;
      } catch (error) {
        enqueueSnackbar('Falha ao salvar vestimenta', { variant: 'error' });
        throw error;
      }
    },
    [garmentsRepository, enqueueSnackbar, refetchGarments]
  );

  const columns: GridColumns = [
    {
      field: 'label',
      headerName: 'Label',
      flex: 1,
    },
    {
      field: 'categoryId',
      headerName: 'Categoria',
      flex: 2,
      editable: true,
      type: 'singleSelect',
      renderCell: (params) => categories.find((category) => category.id === params.row.categoryId)?.label,
      valueOptions: categories.map(({ label, id: categoryId }) => ({ label, value: categoryId })),
    },
    { field: 'price', headerName: 'Price', flex: 1 },
    { field: 'vendor_id', headerName: 'Vendor Id', flex: 1 },
  ];

  return (
    <MainContainer>
      <DataTable
        rows={garments}
        loading={loading}
        columns={columns}
        experimentalFeatures={{ newEditingApi: true }}
        processRowUpdate={processRowUpdate}
        initialState={{ pagination: { pageSize: 25 } }}
      />
    </MainContainer>
  );
};

export default Garments;
